import { call, put, cancelled } from 'redux-saga/effects';
import { config } from '../../api/config';
import { Api } from '../../api/api';
import { actions } from '..';
import { downloadFile, errorMessage } from '../../utils';

const OWNER_TYPE_PRIVATE = 1351;
const OWNER_TYPE_BUSINESS = 1352;

function* enrichOwnerLabel(card) {
  if (!card || card.owner_id == null) {
    return { ...card, owner_label: '' };
  }

  try {
    if (Number(card.owner_type) === OWNER_TYPE_BUSINESS) {
      const businessClient = yield call(Api.getBusinessClient, card.owner_id);
      return {
        ...card,
        owner_label: businessClient.name || String(card.owner_id),
      };
    }

    if (Number(card.owner_type) === OWNER_TYPE_PRIVATE) {
      const client = yield call(Api.getClient, card.owner_id);
      return {
        ...card,
        owner_label: client.phone || String(card.owner_id),
      };
    }
  } catch (e) {
    return {
      ...card,
      owner_label: String(card.owner_id),
    };
  }

  return { ...card, owner_label: String(card.owner_id) };
}

export function* getPhysicalCardsSaga({ body: initialBody }) {
  try {
    yield put(actions.physicalCards.setPhysicalCardsLoadingAction(true));

    const body = {
      ...initialBody,
      limit: config.DEFAULTS.recordsPerPage,
    };

    const { physical_cards, count } = yield call(Api.getPhysicalCards, body);

    yield put(
      actions.physicalCards.setPhysicalCardsAction(physical_cards || [], count)
    );
  } catch (e) {
    const msg = errorMessage(e);
    yield put(actions.error.setErrorAction(msg));
  } finally {
    yield put(actions.physicalCards.setPhysicalCardsLoadingAction(false));
  }
}

export function* searchPhysicalCardsSaga({ query }) {
  try {
    yield put(actions.physicalCards.setPhysicalCardsLoadingAction(true));

    const results = yield call(Api.searchPhysicalCards, query);
    const physicalCards = Array.isArray(results) ? results : [];

    yield put(
      actions.physicalCards.setPhysicalCardsAction(
        physicalCards,
        physicalCards.length
      )
    );
  } catch (e) {
    const msg = errorMessage(e);
    yield put(actions.error.setErrorAction(msg));
  } finally {
    yield put(actions.physicalCards.setPhysicalCardsLoadingAction(false));
  }
}

export function* addPhysicalCardSaga({ body, history }) {
  try {
    yield put(actions.physicalCards.setPhysicalCardsLoadingAction(true));

    yield call(Api.addPhysicalCard, body);

    history.push(config.APP_PATHS.physicalCards);
  } catch (e) {
    const msg = errorMessage(e);
    yield put(actions.error.setErrorAction(msg));
  } finally {
    yield put(actions.physicalCards.setPhysicalCardsLoadingAction(false));
  }
}

export function* getPhysicalCardSaga({ id }) {
  try {
    yield put(actions.physicalCards.setPhysicalCardsLoadingAction(true));

    const card = yield call(Api.getPhysicalCard, id);
    const enriched = yield* enrichOwnerLabel(card);

    yield put(actions.physicalCards.setPhysicalCardAction(enriched));
  } catch (e) {
    const msg = errorMessage(e);
    yield put(actions.error.setErrorAction(msg));
  } finally {
    yield put(actions.physicalCards.setPhysicalCardsLoadingAction(false));
  }
}

function* refreshSelectedCard(card_id, { syncForm = false } = {}) {
  const card = yield call(Api.getPhysicalCard, card_id);
  const enriched = yield* enrichOwnerLabel(card);

  yield put(
    actions.physicalCards.setPhysicalCardAction(
      syncForm ? { ...enriched, _formSyncAt: Date.now() } : enriched
    )
  );
}

export function* updatePhysicalCardSaga({ id, body }) {
  try {
    yield put(actions.physicalCards.setPhysicalCardsLoadingAction(true));

    yield call(Api.updatePhysicalCard, id, body);
    yield* refreshSelectedCard(id, { syncForm: true });
  } catch (e) {
    const msg = errorMessage(e);
    yield put(actions.error.setErrorAction(msg));
  } finally {
    yield put(actions.physicalCards.setPhysicalCardsLoadingAction(false));
  }
}

export function* addPhysicalCardDiscountSaga({ id, body }) {
  try {
    yield put(actions.physicalCards.setPhysicalCardsLoadingAction(true));
    yield put(actions.physicalCards.clearPhysicalCardDiscountErrorAction());

    yield call(Api.updatePhysicalCard, id, body);

    yield put(actions.modals.clearModals());
    yield* refreshSelectedCard(id, { syncForm: true });
  } catch (e) {
    const msg = errorMessage(e);
    // errorMessage returns { msg, code, subMessage }; inline form needs a string
    yield put(
      actions.physicalCards.setPhysicalCardDiscountErrorAction(msg.msg || '')
    );
    yield put(actions.error.setErrorAction(msg));
  } finally {
    yield put(actions.physicalCards.setPhysicalCardsLoadingAction(false));
  }
}

export function* topUpPhysicalCardSaga({ id, body }) {
  try {
    yield put(actions.physicalCards.setPhysicalCardsLoadingAction(true));

    // Uncertain: uses POST /h2_cards/:id/top_up — may need a dedicated physical_cards top-up later.
    yield call(Api.topUpPhysicalCard, id, body);
    yield* refreshSelectedCard(id, { syncForm: true });
  } catch (e) {
    const msg = errorMessage(e);
    yield put(actions.error.setErrorAction(msg));
  } finally {
    yield put(actions.physicalCards.setPhysicalCardsLoadingAction(false));
  }
}

export function* getPhysicalCardUsageSaga({ id, params }) {
  try {
    yield put(actions.physicalCards.setPhysicalCardsLoadingAction(true));

    const { usage, count } = yield call(Api.getPhysicalCardUsage, id, params);

    yield put(
      actions.physicalCards.setPhysicalCardUsageAction(usage || [], count || 0)
    );
  } catch (e) {
    const msg = errorMessage(e);
    yield put(actions.error.setErrorAction(msg));
  } finally {
    yield put(actions.physicalCards.setPhysicalCardsLoadingAction(false));
  }
}

export function* downloadPhysicalCardsImportTemplateSaga() {
  try {
    yield put(
      actions.physicalCards.setPhysicalCardsTemplateDownloadingAction(true)
    );

    const response = yield call(Api.downloadPhysicalCardsImportTemplate);

    downloadFile(response, 'physical_cards_import_template', 'csv');
  } catch (e) {
    const msg = errorMessage(e);
    yield put(actions.error.setErrorAction(msg));
  } finally {
    yield put(
      actions.physicalCards.setPhysicalCardsTemplateDownloadingAction(false)
    );
  }
}

export function* validatePhysicalCardsImportSaga({ file }) {
  try {
    yield put(
      actions.physicalCards.setPhysicalCardsImportValidatingAction(true)
    );
    yield put(actions.physicalCards.setPhysicalCardsImportBatchAction(null));

    const batch = yield call(Api.validatePhysicalCardsImport, file);

    yield put(actions.physicalCards.setPhysicalCardsImportBatchAction(batch));
  } catch (e) {
    const msg = errorMessage(e);
    yield put(actions.error.setErrorAction(msg));
    yield put(actions.physicalCards.setPhysicalCardsImportBatchAction(null));
  } finally {
    if (!(yield cancelled())) {
      yield put(
        actions.physicalCards.setPhysicalCardsImportValidatingAction(false)
      );
    }
  }
}

export function* commitPhysicalCardsImportSaga({ batchId }) {
  try {
    yield put(
      actions.physicalCards.setPhysicalCardsImportCommittingAction(true)
    );
    yield put(
      actions.physicalCards.setPhysicalCardsImportCommitResultAction(null)
    );

    const result = yield call(Api.commitPhysicalCardsImport, batchId);

    yield put(
      actions.physicalCards.setPhysicalCardsImportCommitResultAction({
        success: true,
        imported: result.imported != null ? result.imported : 0,
      })
    );

    yield put(
      actions.physicalCards.getPhysicalCardsAction({
        order: [{ by: 'id', order: 'desc' }],
        offset: 0,
      })
    );
  } catch (e) {
    const msg = errorMessage(e);
    yield put(actions.error.setErrorAction(msg));
    yield put(
      actions.physicalCards.setPhysicalCardsImportCommitResultAction({
        success: false,
        message: msg.msg || 'Importas nepavyko',
      })
    );
  } finally {
    yield put(
      actions.physicalCards.setPhysicalCardsImportCommittingAction(false)
    );
  }
}

export function* downloadPhysicalCardsImportFailedRowsSaga({ batchId }) {
  try {
    const response = yield call(
      Api.downloadPhysicalCardsImportFailedRows,
      batchId
    );

    downloadFile(response, `import_batch_${batchId}_failed`, 'csv');
  } catch (e) {
    const msg = errorMessage(e);
    yield put(actions.error.setErrorAction(msg));
  }
}
